import { supabase } from "@/integrations/supabase/client";
import { CATEGORY_LABELS } from "@/types";

export const fetchAllCategories = async () => {
  try {
    console.log("Fetching all available business categories");
    
    // Option 1: If categories are stored in a separate table
    // const { data, error } = await supabase
    //  .from('categories')
    //  .select('*')
    //  .order('name');
    
    // Option 2: If we're using the predefined categories from the types file
    const categories = Object.keys(CATEGORY_LABELS).map(key => ({
      id: key,
      name: CATEGORY_LABELS[key as keyof typeof CATEGORY_LABELS]
    }));
    
    console.log(`Fetched ${categories.length} categories`);
    return categories;
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

export const fetchPopularTags = async (limit = 10) => {
  try {
    console.log(`Fetching top ${limit} popular tags`);
    
    // This is a simple implementation - in a real app you might have a dedicated tags table
    // or a more sophisticated query to count tag occurrences
    const { data, error } = await supabase
      .from('businesses')
      .select('tags');
    
    if (error) {
      console.error("Error fetching tags:", error);
      throw error;
    }
    
    // Count tag occurrences
    const tagCounts: Record<string, number> = {};
    data?.forEach(business => {
      if (business.tags && Array.isArray(business.tags)) {
        business.tags.forEach((tag: string) => {
          tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        });
      }
    });
    
    // Convert to array and sort by count
    const sortedTags = Object.entries(tagCounts)
      .map(([tag, count]) => ({ tag, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, limit)
      .map(item => item.tag);
    
    console.log(`Found ${sortedTags.length} popular tags`);
    return sortedTags;
  } catch (error) {
    console.error("Error fetching popular tags:", error);
    throw error;
  }
};

/**
 * Format a category name for display
 * @param category The category key to format
 * @returns The formatted category name for display
 */
export const formatCategoryDisplay = (category: string): string => {
  if (!category) return '';
  
  // If the category exists in our predefined labels, use that
  if (category in CATEGORY_LABELS) {
    return CATEGORY_LABELS[category as keyof typeof CATEGORY_LABELS];
  }
  
  // Otherwise, format it nicely (capitalize first letter, replace hyphens with spaces)
  return category
    .replace(/-/g, ' ')
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

/**
 * Get all categories that have existing businesses
 * @returns An array of category strings
 */
export const getExistingCategories = async (): Promise<string[]> => {
  try {
    console.log("Fetching categories with existing businesses");
    
    const { data, error } = await supabase
      .from('businesses')
      .select('category')
      .order('category');
    
    if (error) {
      console.error("Error fetching existing categories:", error);
      throw error;
    }
    
    // Extract unique categories
    const categories = [...new Set(data.map(item => item.category))].filter(Boolean);
    
    console.log(`Found ${categories.length} categories with businesses`);
    return categories;
  } catch (error) {
    console.error("Error fetching existing categories:", error);
    return Object.keys(CATEGORY_LABELS); // Fallback to predefined categories
  }
};
